import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  BackButton,
  DesktopPopupModal,
  ExperiencesAvailabilityCard,
  Icon,
  IconName,
  SanitizedHtml,
} from "halifax";
import {
  ActivitiesDetailsProperties,
  ActivitiesEntryTypeEnum,
  CLICKED_SIMILAR_ACTIVITIES,
  ExperienceAvailability,
  ExperienceId,
  RewardsAccount,
  VIEWED_SIMILAR_ACTIVITIES,
} from "redmond";
import clsx from "clsx";

import styles from "./styles.module.scss";
import { ExperiencesSimilarCard, ExperiencesCompareCard } from "./components";
import {
  COMPARE_ITINERARY,
  SEE_MORE_SIMILAR_EXPERIENCES,
} from "../../textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { getEarnTagText } from "../../../availability/textConstants";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";

export interface IShopSimilar {
  isSkeleton: boolean;
  selectedExperienceInfo?: ExperienceAvailability;
  similarExperiences?: Array<ExperienceAvailability>;
  selectedRewardsAccount?: RewardsAccount;
}

export interface IShopSimilarProps extends IShopSimilar {
  fromDate: Date | null;
  untilDate: Date | null;
  mobile?: boolean;
  rewardsKey?: string;
  disableCheckout?: boolean;
  locationLabel: string | null;
  isTravelApp?: boolean;
  onClick?: (experienceId: ExperienceId) => void;
  activityDetailsProperties?: {
    properties: ActivitiesDetailsProperties;
    encryptedProperties: string[];
  };
  entryType?: ActivitiesEntryTypeEnum;
  entryTypesAll?: ActivitiesEntryTypeEnum[];
}

const SIMILAR_EXPERIENCES_SKELETON: Array<ExperienceAvailability> = Array.from({
  length: 3,
});

export const ShopSimilar = (props: IShopSimilarProps) => {
  const {
    isSkeleton,
    selectedExperienceInfo,
    similarExperiences = SIMILAR_EXPERIENCES_SKELETON,
    selectedRewardsAccount,
    fromDate,
    untilDate,
    mobile = false,
    rewardsKey,
    disableCheckout,
    locationLabel,
    onClick,
    activityDetailsProperties,
    entryType,
    entryTypesAll,
  } = props;

  const [isOverFlow, setIsOverFlow] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedComparedExpId, setSelectedComparedExpId] = useState("");

  // Fire-once gate so we only send the event once per page load
  const hasFiredViewedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const activityDetailsPropertiesRef = useRef(activityDetailsProperties);
  activityDetailsPropertiesRef.current = activityDetailsProperties;

  // Only fire when the section transitions from out-of-view to in-view.
  // The observer is created once after skeleton loading finishes. We track
  // whether the element has ever been out of the viewport before allowing
  // the event to fire. This prevents false positives caused by the page
  // being shorter than its final height while shop options are still loading
  // (the checkout breakdown section hasn't rendered yet, so the similar
  // activities section is temporarily in-viewport before being pushed down).
  useEffect(() => {
    if (isSkeleton) return;

    const node = containerRef.current;
    if (!node) return;

    let hasBeenOutOfView = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) {
          hasBeenOutOfView = true;
          return;
        }
        if (
          hasBeenOutOfView &&
          !hasFiredViewedRef.current &&
          activityDetailsPropertiesRef.current
        ) {
          hasFiredViewedRef.current = true;
          trackEvent({
            eventName: VIEWED_SIMILAR_ACTIVITIES,
            properties: activityDetailsPropertiesRef.current.properties,
            encryptedProperties:
              activityDetailsPropertiesRef.current.encryptedProperties,
          });
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [isSkeleton]);

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const shopSimilarId = "experiences-shop-similar";

  const determineOverFlow = () => {
    const overFlowedElement = document.getElementById(shopSimilarId);
    if (overFlowedElement) {
      return overFlowedElement.clientWidth < overFlowedElement.scrollWidth;
    }
    return false;
  };

  const resizeWindow = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  useEffect(() => {
    if (isSkeleton || !similarExperiences) return;

    const overFlow = determineOverFlow();
    setIsOverFlow(overFlow);
  }, [windowWidth, isSkeleton, similarExperiences]);

  const leftScrollButton = () => {
    const overFlowedElement = document.getElementById(shopSimilarId);
    if (overFlowedElement) {
      overFlowedElement.scrollLeft -= 350;
    }
  };

  const rightScrollButton = () => {
    const overFlowedElement = document.getElementById(shopSimilarId);
    if (overFlowedElement) {
      overFlowedElement.scrollLeft += 350;
    }
  };

  const renderDesktopVersion = () => (
    <div ref={containerRef}>
      <Box className={styles["experiences-shop-similar-container"]}>
        <ExperiencesSimilarCard
          experiencesAvailabilityInfo={selectedExperienceInfo}
          rewardsAccount={selectedRewardsAccount}
          selectedExperienceIdValue={selectedExperienceInfo?.id?.value}
          fromDate={fromDate}
          untilDate={untilDate}
          isSkeleton={isSkeleton}
          disableCheckout={disableCheckout}
          locationLabel={locationLabel}
        />
        <Box className={styles["experiences-shop-other-similar-container"]}>
          {isOverFlow && (
            <BackButton
              aria-label={SEE_MORE_SIMILAR_EXPERIENCES}
              onClick={leftScrollButton}
              className={styles["left-scroll-button"]}
            />
          )}
          <Box
            className={styles["experiences-shop-other-similar-wrapper"]}
            id={shopSimilarId}
          >
            {similarExperiences.map(
              (
                experiencesAvailabilityInfo: ExperienceAvailability,
                expIdx: number,
              ) => (
                <ExperiencesSimilarCard
                  key={experiencesAvailabilityInfo?.id?.value || expIdx}
                  experiencesAvailabilityInfo={experiencesAvailabilityInfo}
                  rewardsAccount={selectedRewardsAccount}
                  selectedExperienceIdValue={selectedExperienceInfo?.id?.value}
                  fromDate={fromDate}
                  untilDate={untilDate}
                  isSkeleton={isSkeleton}
                  onCompareClick={(comparedExpId) => {
                    setSelectedComparedExpId(comparedExpId);
                    setOpenModal(true);
                  }}
                  locationLabel={locationLabel}
                  similarIndex={expIdx}
                  activityDetailsProperties={activityDetailsProperties}
                  entryType={entryType}
                  entryTypesAll={entryTypesAll}
                />
              ),
            )}
          </Box>
          {isOverFlow && (
            <BackButton
              aria-label={SEE_MORE_SIMILAR_EXPERIENCES}
              onClick={rightScrollButton}
              className={styles["right-scroll-button"]}
            />
          )}
        </Box>
      </Box>
      {selectedExperienceInfo?.id?.value && (
        <DesktopPopupModal
          className={styles["experiences-shop-similar-compare-modal-popup"]}
          open={openModal}
          invisibleBackdrop={false}
          onClose={(event: React.MouseEvent) => {
            event.stopPropagation();
            setOpenModal(false);
          }}
          optOutSizeOverride
        >
          <Typography
            variant="h3"
            className={styles["experiences-shop-similar-modal-header"]}
          >
            {COMPARE_ITINERARY}
          </Typography>
          <div className={styles["experiences-shop-similar-modal-content"]}>
            <ExperiencesCompareCard
              experiencesAvailabilityInfo={selectedExperienceInfo}
              selectedExperienceIdValue={selectedExperienceInfo.id.value}
              fromDate={fromDate}
              untilDate={untilDate}
              closeModal={() => setOpenModal(false)}
              locationLabel={locationLabel}
              entryType={entryType}
              entryTypesAll={entryTypesAll}
            />
            <ExperiencesCompareCard
              experiencesAvailabilityInfo={
                similarExperiences.find(
                  (exp) => exp.id.value === selectedComparedExpId,
                ) as ExperienceAvailability
              }
              selectedExperienceIdValue={selectedExperienceInfo.id.value}
              fromDate={fromDate}
              untilDate={untilDate}
              closeModal={() => setOpenModal(false)}
              locationLabel={locationLabel}
              entryType={entryType}
              entryTypesAll={entryTypesAll}
              activityDetailsProperties={activityDetailsProperties}
              similarIndex={similarExperiences.findIndex(
                (exp) => exp.id.value === selectedComparedExpId,
              )}
            />
          </div>
        </DesktopPopupModal>
      )}
    </div>
  );

  const renderMobileVersion = () => (
    <div ref={containerRef}>
      <Box
        className={clsx(
          styles["experiences-shop-other-similar-container"],
          styles.mobile,
        )}
      >
        <Box
          className={styles["experiences-shop-other-similar-wrapper"]}
          id={shopSimilarId}
        >
          {similarExperiences.map(
            (
              experiencesAvailabilityInfo: ExperienceAvailability,
              expIdx: number,
            ) => (
              <ExperiencesAvailabilityCard
                onClick={() => {
                  if (
                    activityDetailsProperties &&
                    experiencesAvailabilityInfo
                  ) {
                    trackEvent({
                      eventName: CLICKED_SIMILAR_ACTIVITIES,
                      properties: {
                        ...activityDetailsProperties.properties,
                        product_id_clicked:
                          experiencesAvailabilityInfo.id?.value,
                        product_name_clicked: experiencesAvailabilityInfo.name,
                        price_clicked:
                          experiencesAvailabilityInfo.bestPrice?.fiat?.value,
                        duration_clicked: experiencesAvailabilityInfo.duration,
                        rating_clicked:
                          experiencesAvailabilityInfo.rating?.reviewAverage,
                        product_index_clicked: expIdx + 1,
                      },
                      encryptedProperties:
                        activityDetailsProperties.encryptedProperties,
                    });
                  }
                  return onClick && onClick(experiencesAvailabilityInfo?.id);
                }}
                key={experiencesAvailabilityInfo?.id?.value ?? expIdx}
                experiencesAvailabilityInfo={experiencesAvailabilityInfo}
                variant="card"
                rewardsKey={rewardsKey}
                earnTagContent={
                  selectedRewardsAccount && (
                    <>
                      <Icon name={IconName.StarIcon} />
                      <SanitizedHtml
                        as={Typography}
                        className="earn-tag-text"
                        html={getEarnTagText(
                            selectedRewardsAccount.earn.experiencesMultiplier,
                            selectedRewardsAccount.rewardsBalance
                              .currencyDescription ??
                              selectedRewardsAccount.rewardsBalance.currency,
                          )}
                      />
                    </>
                  )
                }
                isSkeleton={isSkeleton}
                showCoverImageOnly
                isXmodRedesign={isXmodRedesign}
              />
            ),
          )}
        </Box>
      </Box>
    </div>
  );
  return mobile ? renderMobileVersion() : renderDesktopVersion();
};
